<template>
  <article class="install-card" :class="{ 'install-card--compact': compact }">
    <!-- Image -->
    <div class="install-media">
      <img
        v-if="Install.Thumbnail"
        :src="thumbUrl"
        :alt="Install.Title || 'Install photo'"
        loading="lazy"
      />
      <span v-else class="material-icons install-noimg" aria-hidden="true">image</span>

      <span v-if="isEto" class="install-eto">ETO</span>
      <span v-if="pictureCount" class="install-count">
        <span class="material-icons" aria-hidden="true">photo_library</span>
        {{ pictureCount }}<template v-if="!compact"> {{ pictureCount === 1 ? "photo" : "photos" }}</template>
      </span>
    </div>

    <div class="install-body">
      <!-- Author + date -->
      <div class="install-author">
        <avatar
          class="install-avatar"
          color="#fff"
          :username="fullName"
          :backgroundColor="avatarColor"
        />
        <div class="install-author-text">
          <span class="install-name">{{ fullName }}</span>
          <span v-if="Install.Name" class="install-company">{{ Install.Name }}</span>
        </div>
        <time
          v-if="Install.AddedDate"
          class="install-time"
          :datetime="Install.AddedDate"
          :title="$moment(Install.AddedDate).format('LLL')"
        >
          {{ $moment(Install.AddedDate).fromNow() }}
        </time>
      </div>

      <!-- Market (+ ETO label on large cards) -->
      <div class="install-tags">
        <span v-if="Install.Market" class="install-market">{{ Install.Market }}</span>
      </div>

      <!-- Title (link covers the whole card) -->
      <h3 class="install-title">
        <router-link :to="postLink" class="install-link">{{ Install.Title }}</router-link>
      </h3>

      <p v-if="plainDescription" class="install-desc">{{ plainDescription }}</p>

      <!-- Footer -->
      <div class="install-footer" style="display: flex;">
        <Likes
          :LikeCount="likeCount"
          :isLiked="Install.iLiked != 0"
          :LikedUsers="Install.LikedUsers"
          :PostID="Install.ID"
          style="margin-right: auto;"
        />
        <span class="install-comments" :class="{ 'install-comments--none': !commentCount }" style="margin-left: auto;">
          <span class="material-icons" aria-hidden="true">chat_bubble_outline</span>
          {{ commentCount }}
          <span :class="{ 'sr-only': compact }">{{ commentCount === 1 ? "comment" : "comments" }}</span>
        </span>
        <span class="install-view" aria-hidden="true">
          {{ compact ? "View" : "View post" }} <span class="material-icons">arrow_forward</span>
        </span>
      </div>
    </div>
  </article>
</template>

<script>
import Avatar from "../Atoms/Avatar.vue";
import Likes from "./Likes.vue";

export default {
  name: "InstallCard",
  components: { Avatar, Likes },
  props: {
    Install: { type: Object, required: true },
    // compact = home page card; default = full listing card
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      server: import.meta.env.VITE_APP_SERVER_URL,
    };
  },
  computed: {
    postLink() {
      return `/eto-and-inspiring-installs/${this.Install.ID}`;
    },
    thumbUrl() {
      return `${this.server}/images/${this.Install.ID}/${encodeURIComponent(this.Install.Thumbnail)}`;
    },
    isEto() {
      return Number(this.Install.ETO) === 1;
    },
    pictureCount() {
      return Number(this.Install.PictureCount) || 0;
    },
    fullName() {
      return `${this.Install.FirstName || ""} ${this.Install.LastName || ""}`.trim() || "Unknown";
    },
    likeCount() {
      return Array.isArray(this.Install.LikedUsers) ? this.Install.LikedUsers.length : 0;
    },
    commentCount() {
      return Number(this.Install.MessageCount) || 0;
    },
    // Strip HTML so cutting it short can't break tags; CSS clamps the lines
    plainDescription() {
      const div = document.createElement("div");
      div.innerHTML = this.Install.Description || "";
      return (div.textContent || "").replace(/\s+/g, " ").trim();
    },
    // Same person always gets the same color
    avatarColor() {
      const colors = this.$store.state.colors || ["#429da8"];
      let hash = 0;
      for (const ch of this.fullName) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
      return colors[hash % colors.length];
    },
  },
};
</script>

<style scoped>
.install-card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  border: 1px solid #eef0f2;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.install-card--compact {
  box-shadow: none;
}
.install-card:hover {
  border-color: transparent;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}
.install-card:has(.install-link:focus-visible) {
  outline: 2px solid #429da8;
  outline-offset: 2px;
}

/* Image */
.install-media {
  position: relative;
  aspect-ratio: 16 / 9;
  background: #eceff1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px 8px 0 0;
}
.install-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}
.install-card:hover .install-media img {
  transform: scale(1.03);
}
.install-noimg {
  font-size: 48px;
  color: #c4c9ce;
}
.install-eto {
  position: absolute;
  top: 12px;
  left: 12px;
  background: #1e3040;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  padding: 5px 10px;
  border-radius: 4px;
}
.install-count {
  position: absolute;
  bottom: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 13px;
  padding: 3px 8px;
  border-radius: 999px;
}
.install-count .material-icons {
  font-size: 15px;
}

/* Body */
.install-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 18px 20px 16px;
}
.install-card--compact .install-body {
  padding: 16px 18px 14px;
}

/* Author */
.install-author {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.install-avatar {
  flex-shrink: 0;
  width: 36px !important;
  height: 36px !important;
  font-size: 14px !important;
}
.install-author-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;
}
.install-name {
  font-weight: 600;
  font-size: 14px;
  color: #1e3040;
}
.install-company {
  font-size: 13px;
  color: #737373;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.install-time {
  margin-left: auto;
  font-size: 13px;
  color: #8a96a3;
  white-space: nowrap;
}

/* Tags */
.install-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.install-tags:empty {
  display: none;
}
.install-market,
.install-eto-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 13px;
  font-weight: 500;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.install-market {
  background: #e8f3f4;
  color: #2c7b85;
}
.install-eto-tag {
  background: #e9ecef;
  color: #1e3040;
}
.install-eto-tag .material-icons {
  font-size: 15px;
}

/* Title + description */
.install-title {
  font-size: 21px;
  font-weight: 500;
  line-height: 1.3;
  margin: 0 0 6px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.install-card--compact .install-title {
  font-size: 19px;
}
.install-link,
.install-link:hover,
.install-link:focus {
  color: #1e3040;
  text-decoration: none !important;
  outline: none;
}
/* Whole card clickable */
.install-link::after {
  content: "";
  position: absolute;
  inset: 0;
}
.install-desc {
  color: #575757;
  font-size: 15px;
  line-height: 1.55;
  margin: 0 0 18px;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.install-card--compact .install-desc {
  -webkit-line-clamp: 2;
  line-height: 1.5;
}

/* Footer: sits above the card link so Like stays clickable */
.install-footer {
  position: relative;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: auto; /* keeps footers lined up across cards of different heights */
  font-size: 14px;
  pointer-events: none; /* clicks fall through to the card link... */
}
.install-footer > :first-child {
  pointer-events: auto;  /* ...except the Likes button */
  flex: 1 1 auto;
  min-width: 0;
}
.install-comments {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #4d5967;
}
.install-comments--none {
  color: #a3abb3;
}
.install-comments .material-icons {
  font-size: 17px;
}
.install-view {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: #429da8;
  font-weight: 500;
}
.install-view .material-icons {
  font-size: 16px;
  transition: transform 0.2s;
}
.install-card:hover .install-view .material-icons {
  transform: translateX(3px);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
/* Footer: sits above the card link so Like stays clickable */
.install-footer {
  position: relative;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: auto;
  padding-top: 14px;
  border-top: 1px solid #eef0f2;
  font-size: 14px;
  pointer-events: none; /* clicks fall through to the card link... */
}
.install-footer > :first-child {
  pointer-events: auto;  /* ...except the Likes button */
  flex: 1 1 auto;
  min-width: 0;          /* lets long "liked by" text shrink instead of squeezing the rest */
}
.install-comments,
.install-view {
  flex-shrink: 0;
  white-space: nowrap;
}
.install-comments {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #4d5967;
}
.install-comments--none {
  color: #a3abb3;
}
.install-comments .material-icons {
  font-size: 17px;
}
.install-view {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: #429da8;
  font-weight: 500;
}
.install-view .material-icons {
  font-size: 16px;
  transition: transform 0.2s;
}
.install-card:hover .install-view .material-icons {
  transform: translateX(3px);
}
</style>
